'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Cormorant_Garamond, Inter } from 'next/font/google';
import styles from './landing.module.css';

const cormorant = Cormorant_Garamond({
  subsets: ['latin'],
  weight: ['300', '400', '600'],
  style: ['normal', 'italic'],
  variable: '--font-cormorant',
});

const inter = Inter({
  subsets: ['latin'],
  weight: ['300', '400', '500'],
  variable: '--font-inter',
});

type Lang = 'en' | 'fr';

export default function LandingPage() {
  const [lang, setLang] = useState<Lang>('en');
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setLang(navigator.language.startsWith('fr') ? 'fr' : 'en');
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (navRef.current) {
        navRef.current.style.boxShadow =
          window.scrollY > 20 ? '0 4px 40px rgba(0,0,0,0.4)' : 'none';
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const t = (en: React.ReactNode, fr: React.ReactNode) => (lang === 'fr' ? fr : en);

  return (
    <div className={`${cormorant.variable} ${inter.variable} ${styles.page}`}>
      {/* NAV */}
      <nav ref={navRef} className={styles.nav}>
        <a href="#" className={styles['nav-logo']}>
          <Image
            src="/images/ozclub-logo-white.png"
            alt="Oz Club"
            height={36}
            width={120}
            style={{ objectFit: 'contain' }}
          />
        </a>
        <ul className={styles['nav-links']}>
          <li><a href="#chateaux">{t('Chateaux', 'Châteaux')}</a></li>
          <li><a href="#restaurants">Restaurants</a></li>
          <li><a href="#campaigns">{t('Campaigns', 'Promotions')}</a></li>
          <li><a href="#ozclub">Oz Club</a></li>
        </ul>
        <div className={styles['nav-cta']}>
          <Link href="/signin" className={styles['btn-outline']}>
            {t('Sign In', 'Connexion')}
          </Link>
          <Link href="/auth/signup" className={styles['btn-solid']}>
            {t('Join Free', 'Rejoindre')}
          </Link>
        </div>
      </nav>

      {/* HERO */}
      <section className={styles.hero}>
        <div>
          <div className={styles['hero-eyebrow']}>
            {t('Occitanie · South France · Wine Platform', 'Occitanie · Sud de France · Plateforme Viticole')}
          </div>
          <h1 className={styles['hero-title']}>
            <Image
              src="/images/ozclub-logo-white.png"
              alt="Oz Club — Wizards of Vine"
              width={420}
              height={157}
              className={styles['hero-logo']}
              priority
            />
          </h1>
          <p className={styles['hero-sub']}>
            {t(
              "Where Occitanie's finest vineyards meet the restaurants and wine lovers who deserve them.",
              "Là où les meilleurs vignobles d'Occitanie rencontrent les restaurants et les amoureux du vin qui le méritent.",
            )}
          </p>
          <div className={styles['hero-actions']}>
            <Link href="/auth/signup/chateau" className={styles['hero-btn-primary']}>
              {t('Join as Château', 'Rejoindre en tant que Château')}
            </Link>
            <Link href="/auth/signup/restaurant" className={styles['hero-btn-secondary']}>
              {t('Join as Restaurant', 'Rejoindre en tant que Restaurant')}
            </Link>
          </div>
          <div className={styles['hero-stats']}>
            <div>
              <span className={styles['hero-stat-number']}>700+</span>
              <span className={styles['hero-stat-label']}>
                {t('Occitanie Wineries', "Domaines d'Occitanie")}
              </span>
            </div>
            <div>
              <span className={styles['hero-stat-number']}>€12B</span>
              <span className={styles['hero-stat-label']}>
                {t('French Wine Market', 'Marché du Vin Français')}
              </span>
            </div>
            <div>
              <span className={styles['hero-stat-number']}>100%</span>
              <span className={styles['hero-stat-label']}>
                {t('Direct from Producer', 'Direct Producteur')}
              </span>
            </div>
          </div>
          <div className={styles['scroll-hint']}>Scroll</div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className={`${styles['how-bg']} ${styles.section}`} id="how">
        <div className={styles.container}>
          <div className={styles['how-eyebrow']}>
            {t('How it works', 'Comment ça fonctionne')}
          </div>
          <h2 className={styles['how-section-title']}>
            {t(<>Direct. Simple. <em>Honest.</em></>, <>Direct. Simple. <em>Honnête.</em></>)}
          </h2>
          <p className={styles['how-section-body']}>
            {t(
              'No distributors. No middlemen. Châteaux set their own prices, restaurants order directly, and everyone earns more.',
              "Pas de distributeurs. Pas d'intermédiaires. Les châteaux fixent leurs propres prix, les restaurants commandent directement, et tout le monde gagne plus.",
            )}
          </p>
          <div className={styles['how-grid']}>
            <div className={styles['how-card']}>
              <span className={styles['how-number']}>01</span>
              <span className={styles['how-icon']}><i className="fa-solid fa-wine-bottle"></i></span>
              <h3 className={styles['how-card-title']}>
                {t('Château lists wines', 'Le château liste ses vins')}
              </h3>
              <p className={styles['how-card-body']}>
                {t(
                  'Upload your wine catalogue, set B2B and retail prices separately, create campaigns. Your wines, your rules.',
                  'Chargez votre catalogue, définissez vos tarifs B2B et grand public, créez des promotions. Vos vins, vos règles.',
                )}
              </p>
            </div>
            <div className={styles['how-card']}>
              <span className={styles['how-number']}>02</span>
              <span className={styles['how-icon']}><i className="fa-solid fa-utensils"></i></span>
              <h3 className={styles['how-card-title']}>
                {t('Restaurant discovers & orders', 'Le restaurant découvre & commande')}
              </h3>
              <p className={styles['how-card-body']}>
                {t(
                  'Browse by appellation, vintage, or producer. Order directly. Promotional deals apply automatically at checkout.',
                  'Parcourez par appellation, millésime ou producteur. Commandez directement. Les promotions s’appliquent automatiquement.',
                )}
              </p>
            </div>
            <div className={styles['how-card']}>
              <span className={styles['how-number']}>03</span>
              <span className={styles['how-icon']}><i className="fa-solid fa-truck"></i></span>
              <h3 className={styles['how-card-title']}>
                {t('Delivered & invoiced', 'Livré & facturé')}
              </h3>
              <p className={styles['how-card-body']}>
                {t(
                  'The château ships directly. A compliant French fiscal invoice is generated automatically. Full order history in your dashboard.',
                  'Le château livre directement. Une facture fiscale française est générée automatiquement. Historique complet dans votre tableau de bord.',
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CHATEAUX */}
      <section className={styles.section} id="chateaux">
        <div className={styles.container}>
          <div className={styles['section-eyebrow']}>
            {t('Featured Producers', 'Producteurs Partenaires')}
          </div>
          <h2 className={styles['section-title']}>
            {t(<>Occitanie&apos;s <em>finest</em> vineyards</>, <>Les meilleurs vignobles <em>d&apos;Occitanie</em></>)}
          </h2>
          <p className={styles['section-body']}>
            {t(
              'From the Pyrenean foothills to the Mediterranean coast — real producers, real wines, real stories.',
              'Des contreforts pyrénéens à la côte méditerranéenne — de vrais producteurs, de vrais vins, de vraies histoires.',
            )}
          </p>
          <div className={styles['chateaux-grid']}>
            <div className={styles['chateau-card']}>
              <div className={styles['chateau-photo']}>
                <img
                  src="https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=800&q=80"
                  alt="Domaine Riberach"
                />
                <div className={styles['chateau-photo-overlay']}>
                  <span className={styles['chateau-badge']}><i className="fa-solid fa-leaf"></i> Agro-Ecology · Organic</span>
                  <h3 className={styles['chateau-name']}>Domaine Riberach</h3>
                  <div className={styles['chateau-appellation']}>Pyrénées Orientales · Bélesta</div>
                </div>
              </div>
              <div className={styles['chateau-body']}>
                <p className={styles['chateau-desc']}>
                  {t(
                    'A former "wine cathedral" from 1925, reimagined by architect couple Luc Richard and Karin Pühringer. Spontaneous fermentations, zero synthetic inputs, and the freshness of high-altitude Pyrenean terroir.',
                    'Une ancienne "cathédrale du vin" de 1925, réinterprétée par les architectes Luc Richard et Karin Pühringer. Fermentations spontanées, zéro intrant de synthèse, et la fraîcheur des terroirs pyrénéens d’altitude.',
                  )}
                </p>
                <div className={styles['chateau-tags']}>
                  <span className={styles['chateau-tag']}>No pesticides</span>
                  <span className={styles['chateau-tag']}>Spontaneous fermentation</span>
                  <span className={styles['chateau-tag']}>Minimal sulphites</span>
                  <span className={styles['chateau-tag']}>Mountain terroir</span>
                </div>
                <div className={styles['chateau-footer']}>
                  <div className={styles['chateau-wines']}>
                    <strong><i className="fa-solid fa-wine-glass"></i></strong> Rouge · Blanc · Rosé
                  </div>
                  <Link href="/auth/signup" className={styles['chateau-link']}>
                    {t('Order wines →', 'Commander →')}
                  </Link>
                </div>
              </div>
            </div>

            <div className={styles['chateau-card']}>
              <div className={styles['chateau-photo']}>
                <img
                  src="https://images.unsplash.com/photo-1474722883778-792e7990302f?w=800&q=80"
                  alt="Château L'Hospitalet"
                />
                <div className={styles['chateau-photo-overlay']}>
                  <span className={styles['chateau-badge']}><i className="fa-solid fa-star"></i> Wine Resort · Michelin Green Star</span>
                  <h3 className={styles['chateau-name']}>Château L&apos;Hospitalet</h3>
                  <div className={styles['chateau-appellation']}>La Clape AOP · Narbonne</div>
                </div>
              </div>
              <div className={styles['chateau-body']}>
                <p className={styles['chateau-desc']}>
                  {t(
                    "Gérard Bertrand's iconic 1,000-hectare Wine Resort on the Mediterranean coast. 41 rooms, 3 restaurants, spa — and 90+ Occitanie wines across 16 estates. Nature as the cradle of the art of living.",
                    'Le légendaire Wine Resort de 1 000 hectares de Gérard Bertrand sur la côte méditerranéenne. 41 chambres, 3 restaurants, spa — et 90+ vins d’Occitanie sur 16 domaines. La Nature, écrin de l’Art de Vivre.',
                  )}
                </p>
                <div className={styles['chateau-tags']}>
                  <span className={styles['chateau-tag']}>Corbières</span>
                  <span className={styles['chateau-tag']}>Minervois</span>
                  <span className={styles['chateau-tag']}>5-star estate</span>
                  <span className={styles['chateau-tag']}>Oenotourism</span>
                </div>
                <div className={styles['chateau-footer']}>
                  <div className={styles['chateau-wines']}>
                    <strong><i className="fa-solid fa-wine-glass"></i></strong> Rouge · Blanc · Rosé · Sparkling
                  </div>
                  <Link href="/auth/signup" className={styles['chateau-link']}>
                    {t('Order wines →', 'Commander →')}
                  </Link>
                </div>
              </div>
            </div>

            <div className={styles['chateau-card']}>
              <div className={styles['chateau-photo']}>
                <img
                  src="https://images.unsplash.com/photo-1559519529-0936e4058364?w=800&q=80"
                  alt="Château Capitoul"
                />
                <div className={styles['chateau-photo-overlay']}>
                  <span className={styles['chateau-badge']}><i className="fa-solid fa-star"></i> Robert Parker 93+ · Decanter 92</span>
                  <h3 className={styles['chateau-name']}>Château Capitoul</h3>
                  <div className={styles['chateau-appellation']}>La Clape AOP · Narbonne · Est. Roman era</div>
                </div>
              </div>
              <div className={styles['chateau-body']}>
                <p className={styles['chateau-desc']}>
                  {t(
                    "Known to Julius Caesar himself, elevated to La Clape premier cru in 2015. 64 hectares stretching to the lagoon's edge — clay, limestone, Mediterranean freshness. Award-winning wines of power and elegance.",
                    'Connu de Jules César, élevé au rang de Cru La Clape en 2015. 64 hectares s’étendant jusqu’à la lagune — argile, calcaire, fraîcheur méditerranéenne. Des vins primés, puissants et élégants.',
                  )}
                </p>
                <div className={styles['chateau-tags']}>
                  <span className={styles['chateau-tag']}>La Clape Premier Cru</span>
                  <span className={styles['chateau-tag']}>Grenache · Mourvèdre · Syrah</span>
                  <span className={styles['chateau-tag']}>Bourboulenc blanc</span>
                </div>
                <div className={styles['chateau-footer']}>
                  <div className={styles['chateau-wines']}>
                    <strong><i className="fa-solid fa-wine-glass"></i></strong> Rouge · Blanc · Rosé
                  </div>
                  <Link href="/auth/signup" className={styles['chateau-link']}>
                    {t('Order wines →', 'Commander →')}
                  </Link>
                </div>
              </div>
            </div>

            <div className={styles['chateau-card']}>
              <div className={styles['chateau-photo']}>
                <img
                  src="https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=800&q=80"
                  alt="Château Catherine de Montgolfier"
                />
                <div className={styles['chateau-photo-overlay']}>
                  <span className={styles['chateau-badge']}><i className="fa-solid fa-leaf"></i> 21 Œufs de Beaune · 100% Bio</span>
                  <h3 className={styles['chateau-name']}>Château Catherine de Montgolfier</h3>
                  <div className={styles['chateau-appellation']}>La Clape AOP · Armissan · Bio</div>
                </div>
              </div>
              <div className={styles['chateau-body']}>
                <p className={styles['chateau-desc']}>
                  {t(
                    'A family tribute to an extraordinary mother. 100% organic La Clape wines aged in the largest collection of Œufs de Beaune in France — 21 concrete eggs producing wines of exceptional delicacy. Familiae Spiritu.',
                    'Un hommage familial à une mère extraordinaire. Vins de La Clape 100% bio élevés dans la plus grande collection d’Œufs de Beaune de France — 21 œufs béton pour des vins d’une délicatesse exceptionnelle. Familiae Spiritu.',
                  )}
                </p>
                <div className={styles['chateau-tags']}>
                  <span className={styles['chateau-tag']}>100% Organic</span>
                  <span className={styles['chateau-tag']}>Art Centre</span>
                  <span className={styles['chateau-tag']}>Chambres d&apos;hôtes</span>
                  <span className={styles['chateau-tag']}>Béton eggs</span>
                </div>
                <div className={styles['chateau-footer']}>
                  <div className={styles['chateau-wines']}>
                    <strong><i className="fa-solid fa-wine-glass"></i></strong> Rouge · Blanc · Rosé
                  </div>
                  <Link href="/auth/signup" className={styles['chateau-link']}>
                    {t('Order wines →', 'Commander →')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RESTAURANTS */}
      <section className={`${styles['restaurants-bg']} ${styles.section}`} id="restaurants">
        <div className={styles.container}>
          <div className={styles['section-eyebrow']}>
            {t('Partner Restaurants', 'Restaurants Partenaires')}
          </div>
          <h2 className={styles['section-title']}>
            {t(<>Tables that <em>care</em> about wine</>, <>Des tables qui <em>aiment</em> le vin</>)}
          </h2>
          <p className={styles['section-body']}>
            {t(
              "From Narbonne's lakeside to Perpignan's gastronomic heights — restaurants who order smarter with Oz Club.",
              'De la brasserie au bord du lac de Narbonne aux hauteurs gastronomiques de Perpignan — des restaurants qui commandent plus intelligemment avec Oz Club.',
            )}
          </p>
          <div className={styles['restaurants-grid']}>
            <div className={styles['restaurant-card']}>
              <div className={styles['restaurant-photo']}>
                <img
                  src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&q=80"
                  alt="Le Petit Lac"
                />
                <div className={styles['restaurant-photo-overlay']}>
                  <div className={styles['restaurant-city']}>Narbonne</div>
                  <h3 className={styles['restaurant-name']}>Le Petit Lac</h3>
                </div>
              </div>
              <div className={styles['restaurant-body']}>
                <div className={styles['restaurant-type']}>
                  {t('Brasserie · Lakeside terrace', 'Brasserie · Terrasse au lac')}
                </div>
                <p className={styles['restaurant-desc']}>
                  {t(
                    "Panoramic terrace on the water's edge. Chef Marc Schwall's daily-renewed menu celebrates fresh local products. Maître Restaurateur label.",
                    'Terrasse panoramique au bord de l’eau. Le menu du Chef Marc Schwall, renouvelé chaque jour, célèbre les produits frais locaux. Maître Restaurateur.',
                  )}
                </p>
              </div>
            </div>
            <div className={styles['restaurant-card']}>
              <div className={styles['restaurant-photo']}>
                <img
                  src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&q=80"
                  alt="Ô Juste"
                />
                <div className={styles['restaurant-photo-overlay']}>
                  <div className={styles['restaurant-city']}>Narbonne</div>
                  <h3 className={styles['restaurant-name']}>Ô Juste</h3>
                </div>
              </div>
              <div className={styles['restaurant-body']}>
                <div className={styles['restaurant-type']}>
                  {t('Bistrot · Short changing menu', 'Bistrot · Carte courte changeante')}
                </div>
                <p className={styles['restaurant-desc']}>
                  {t(
                    'A warm counter bistrot in the heart of Narbonne. Local, reasoned cuisine. Short changing menu, market products, direct sourcing from producers.',
                    'Un bistrot comptoir chaleureux au cœur de Narbonne. Cuisine locale et raisonnée. Carte courte changeante, produits du marché, circuit court.',
                  )}
                </p>
              </div>
            </div>
            <div className={styles['restaurant-card']}>
              <div className={styles['restaurant-photo']}>
                <img
                  src="https://images.unsplash.com/photo-1559339352-11d035aa65de?w=600&q=80"
                  alt="Le Patio"
                />
                <div className={styles['restaurant-photo-overlay']}>
                  <div className={styles['restaurant-city']}>Béziers</div>
                  <h3 className={styles['restaurant-name']}>Le Patio</h3>
                </div>
              </div>
              <div className={styles['restaurant-body']}>
                <div className={styles['restaurant-type']}>
                  {t('Mediterranean · Garden setting', 'Méditerranéen · Cadre jardin')}
                </div>
                <p className={styles['restaurant-desc']}>
                  {t(
                    'A patio restaurant in the heart of Béziers, celebrating the flavours of Mediterranean cuisine with seasonal, local ingredients and a carefully curated wine list.',
                    'Un restaurant à patio au cœur de Béziers, célébrant les saveurs méditerranéennes avec des produits locaux et saisonniers et une carte des vins soigneusement sélectionnée.',
                  )}
                </p>
              </div>
            </div>
            <div className={styles['restaurant-card']}>
              <div className={styles['restaurant-photo']}>
                <img
                  src="https://images.unsplash.com/photo-1424847651672-bf20a4b0982b?w=600&q=80"
                  alt="Villa Duflot"
                />
                <div className={styles['restaurant-photo-overlay']}>
                  <div className={styles['restaurant-city']}>Perpignan</div>
                  <h3 className={styles['restaurant-name']}>Villa Duflot</h3>
                </div>
              </div>
              <div className={styles['restaurant-body']}>
                <div className={styles['restaurant-type']}>
                  {t('Gastronomic · Hotel restaurant', "Gastronomique · Restaurant d'hôtel")}
                </div>
                <p className={styles['restaurant-desc']}>
                  {t(
                    'Fine dining at Les Collectionneurs, Perpignan. Catalan terroir between Pyrenees and Mediterranean. Vinotheque with 150+ references, refined 1950s-inspired setting.',
                    'Gastronomie fine aux Collectionneurs, Perpignan. Terroir catalan entre Pyrénées et Méditerranée. Vinothèque avec 150+ références, cadre raffiné inspiré des années 50.',
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CAMPAIGNS */}
      <section className={`${styles['campaign-bg']} ${styles.section}`} id="campaigns">
        <div className={styles.container}>
          <div className={styles['campaign-eyebrow']}>
            {t('Smart Promotions', 'Promotions Intelligentes')}
          </div>
          <h2 className={styles['campaign-section-title']}>
            {t(<>Campaigns that <em>work</em></>, <>Des promotions qui <em>fonctionnent</em></>)}
          </h2>
          <p className={styles['campaign-section-body']}>
            {t(
              'Chateaux create flexible promotion rules. Restaurants benefit automatically at checkout. No coupon codes, no manual work.',
              'Les châteaux créent des règles promotionnelles flexibles. Les restaurants en bénéficient automatiquement à la commande. Pas de codes promo, pas de travail manuel.',
            )}
          </p>
          <div className={styles['campaign-grid']}>
            <div className={styles['campaign-card']}>
              <span className={styles['campaign-icon']}><i className="fa-solid fa-gift"></i></span>
              <div className={styles['campaign-rule']}>
                {t(<>Buy <strong>5 bottles</strong>, get the <strong>6th free</strong></>, <>Achetez <strong>5 bouteilles</strong>, la <strong>6ème offerte</strong></>)}
              </div>
              <p className={styles['campaign-sub']}>
                {t(
                  'Volume reward. Applied automatically. Same wine, zero friction.',
                  'Récompense volume. Appliquée automatiquement. Même vin, zéro friction.',
                )}
              </p>
            </div>
            <div className={styles['campaign-card']}>
              <span className={styles['campaign-icon']}><i className="fa-solid fa-truck"></i></span>
              <div className={styles['campaign-rule']}>
                {t(<>Order <strong>12 bottles</strong>, get <strong>free delivery</strong></>, <>Commandez <strong>12 bouteilles</strong>, <strong>livraison offerte</strong></>)}
              </div>
              <p className={styles['campaign-sub']}>
                {t(
                  'Encourages larger orders. Every château sets their own threshold.',
                  'Encourage les commandes plus importantes. Chaque château fixe son propre seuil.',
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OZ CLUB */}
      <section className={`${styles['ozclub-bg']} ${styles.section}`} id="ozclub">
        <i className={`fa-solid fa-hat-wizard ${styles['ozclub-bg-icon']}`}></i>
        <div className={styles.container}>
          <div className={styles['ozclub-inner']}>
            <div>
              <div className={styles['ozclub-eyebrow']}>Phase 3 · Coming 2027</div>
              <h2 className={styles['ozclub-title']}>
                Oz Club<br />
                <em>Wizards of Vine</em>
              </h2>
              <p className={styles['ozclub-tagline']}>
                {t(
                  'Every month, our sommeliers handpick 6 organic Occitanie wines — delivered to your door, with free delivery, always.',
                  'Chaque mois, nos sommeliers sélectionnent 6 vins bio d’Occitanie — livrés chez vous, avec livraison gratuite, toujours.',
                )}
              </p>
              <Link href="/auth/signup" className={styles['ozclub-cta']}>
                {t('Join the Waitlist', "Rejoindre la liste d'attente")}
              </Link>
            </div>
            <div className={styles['ozclub-tiers']}>
              <div className={styles['ozclub-tier']}>
                <div className={styles['tier-left']}>
                  <span className={styles['tier-icon']}><i className="fa-solid fa-medal"></i></span>
                  <div>
                    <div className={styles['tier-name']}>Apprentice</div>
                    <div className={styles['tier-desc']}>
                      {t('6 bottles · Sommelier selection', '6 bouteilles · Sélection sommelier')}
                    </div>
                  </div>
                </div>
                <div className={styles['tier-price']}>€29<span>/mo</span></div>
              </div>
              <div className={styles['ozclub-tier']}>
                <div className={styles['tier-left']}>
                  <span className={styles['tier-icon']}><i className="fa-solid fa-medal"></i></span>
                  <div>
                    <div className={styles['tier-name']}>Wizard</div>
                    <div className={styles['tier-desc']}>
                      {t('6 + 1 rare bottle · Priority tastings', '6 + 1 bouteille rare · Dégustations prioritaires')}
                    </div>
                  </div>
                </div>
                <div className={styles['tier-price']}>€49<span>/mo</span></div>
              </div>
              <div className={styles['ozclub-tier']}>
                <div className={styles['tier-left']}>
                  <span className={styles['tier-icon']}><i className="fa-solid fa-medal"></i></span>
                  <div>
                    <div className={styles['tier-name']}>Grand Wizard</div>
                    <div className={styles['tier-desc']}>
                      {t('Grand Cru selection · Vineyard visit credits', 'Sélection Grand Cru · Crédits visites de vignobles')}
                    </div>
                  </div>
                </div>
                <div className={styles['tier-price']}>€79<span>/mo</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REGIONS */}
      <section className={`${styles['map-teaser']} ${styles.section}`}>
        <div className={styles.container}>
          <div className={styles['section-eyebrow']} style={{ textAlign: 'center' }}>
            {t('Our Territory', 'Notre Territoire')}
          </div>
          <h2 className={styles['section-title']} style={{ textAlign: 'center' }}>
            {t(<>The appellations of <em>Occitanie</em></>, <>Les appellations de <em>l&apos;Occitanie</em></>)}
          </h2>
          <div className={styles['regions-grid']}>
            <span className={styles['region-pill']}>Corbières AOP</span>
            <span className={styles['region-pill']}>Minervois AOP</span>
            <span className={styles['region-pill']}>La Clape AOP</span>
            <span className={styles['region-pill']}>Fitou AOP</span>
            <span className={styles['region-pill']}>Languedoc AOP</span>
            <span className={styles['region-pill']}>Roussillon AOP</span>
            <span className={styles['region-pill']}>Picpoul de Pinet</span>
            <span className={styles['region-pill']}>Saint-Chinian AOP</span>
            <span className={styles['region-pill']}>Faugères AOP</span>
            <span className={styles['region-pill']}>Maury AOP</span>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className={styles['final-cta']}>
        <h2 className={styles['final-cta-title']}>
          {t(<>The wines of Occitanie deserve <em>better</em></>, <>Les vins d&apos;Occitanie méritent <em>mieux</em></>)}
        </h2>
        <p className={styles['final-cta-sub']}>
          {t(
            'Join the platform connecting Occitanie’s chateaux with the restaurants and wine lovers who deserve them.',
            "Rejoignez la plateforme qui connecte les châteaux d'Occitanie avec les restaurants et les amoureux du vin qui le méritent.",
          )}
        </p>
        <div className={styles['cta-split']}>
          <Link href="/auth/signup/chateau" className={styles['cta-box']}>
            <span className={styles['cta-box-icon']}><i className="fa-solid fa-wine-bottle"></i></span>
            <div className={styles['cta-box-title']}>
              {t("I'm a Château", 'Je suis un Château')}
            </div>
            <p className={styles['cta-box-sub']}>
              {t('List your wines, reach restaurants directly', 'Listez vos vins, atteignez les restaurants directement')}
            </p>
            <span className={styles['cta-box-btn']}>
              {t('Get started free', 'Commencer gratuitement')}
            </span>
          </Link>
          <Link href="/auth/signup/restaurant" className={styles['cta-box']}>
            <span className={styles['cta-box-icon']}><i className="fa-solid fa-utensils"></i></span>
            <div className={styles['cta-box-title']}>
              {t("I'm a Restaurant", 'Je suis un Restaurant')}
            </div>
            <p className={styles['cta-box-sub']}>
              {t('Order direct from chateaux, better prices', 'Commandez directement aux châteaux, meilleurs prix')}
            </p>
            <span className={styles['cta-box-btn']}>
              {t('Start ordering', 'Commencer à commander')}
            </span>
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className={styles.footer}>
        <a href="#" className={styles['footer-logo']}>
          <Image
            src="/images/ozclub-logo-white.png"
            alt="Oz Club"
            height={36}
            width={120}
            style={{ objectFit: 'contain' }}
          />
        </a>
        <p className={styles['footer-tagline']}>
          {t(
            'From the vineyards of Narbonne to the tables of France.',
            'Des vignobles de Narbonne aux tables de France.',
          )}
        </p>
        <ul className={styles['footer-links']}>
          <li><Link href="/signin">{t('Sign In', 'Connexion')}</Link></li>
          <li><Link href="/auth/signup">{t('Register', "S'inscrire")}</Link></li>
          <li><a href="#chateaux">{t('Chateaux', 'Châteaux')}</a></li>
          <li><a href="#ozclub">Oz Club</a></li>
        </ul>
        <span className={styles['footer-legal']}>© 2026 Oz Club · Occitanie, France</span>
      </footer>

      {/* LANG SWITCHER */}
      <div className={styles['lang-switch']}>
        <button
          type="button"
          className={`${styles['lang-btn']} ${lang === 'en' ? styles.active : ''}`}
          onClick={() => setLang('en')}
        >
          EN
        </button>
        <button
          type="button"
          className={`${styles['lang-btn']} ${lang === 'fr' ? styles.active : ''}`}
          onClick={() => setLang('fr')}
        >
          FR
        </button>
      </div>
    </div>
  );
}
